import { FileEntry } from '../stores/files.store';

export class FileSystemService {
  private static instance: FileSystemService;

  private constructor() {}

  public static getInstance(): FileSystemService {
    if (!FileSystemService.instance) {
      FileSystemService.instance = new FileSystemService();
    }
    return FileSystemService.instance;
  }

  async selectDirectory(): Promise<FileSystemDirectoryHandle> {
    return await window.showDirectoryPicker({
      mode: 'readwrite',
    });
  }

  async readDirectory(handle: FileSystemDirectoryHandle, basePath = ''): Promise<FileEntry[]> {
    const entries: FileEntry[] = [];
    for await (const entry of (handle as any).values()) {
      let size = 0;
      let modifiedTime = new Date();
      const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
      
      if (entry.kind === 'file') {
        try {
          const file = await (entry as FileSystemFileHandle).getFile();
          size = file.size;
          modifiedTime = new Date(file.lastModified);
        } catch (e) {
          console.warn(`Failed to get file info for ${entry.name}`, e);
        }
      }

      entries.push({
        name: entry.name,
        path: relativePath,
        type: entry.kind === 'directory' ? 'directory' : 'file',
        size,
        modifiedTime,
        handle: entry,
        provider: 'local'
      });
    }
    return entries;
  }

  async searchFiles(handle: FileSystemDirectoryHandle, query: string, signal?: AbortSignal): Promise<FileEntry[]> {
    const results: FileEntry[] = [];
    const lowerQuery = query.toLowerCase();

    async function scan(currentHandle: FileSystemDirectoryHandle, currentPath: string) {
      if (signal?.aborted) return;

      for await (const entry of (currentHandle as any).values()) {
        if (signal?.aborted) return;

        const relativePath = currentPath ? `${currentPath}/${entry.name}` : entry.name;

        if (entry.name.toLowerCase().includes(lowerQuery)) {
          let size = 0;
          let modifiedTime = new Date();
          if (entry.kind === 'file') {
            const file = await (entry as FileSystemFileHandle).getFile();
            size = file.size;
            modifiedTime = new Date(file.lastModified);
          }
          results.push({
            name: entry.name,
            path: relativePath,
            type: entry.kind === 'directory' ? 'directory' : 'file',
            size,
            modifiedTime,
            handle: entry,
            provider: 'local'
          });
        }

        if (entry.kind === 'directory') {
          await scan(entry, relativePath);
        }
      }
    }

    await scan(handle, '');

    // 去重：如果文件在某个匹配的目录下，则移除该文件
    const directories = results.filter(e => e.type === 'directory').map(e => e.path);
    if (directories.length === 0) {
      return results;
    }

    return results.filter(entry => {
      if (entry.type === 'directory') {
        return true;
      }
      // 检查文件是否在某个匹配的目录下
      const isInMatchedDir = directories.some(dir => entry.path.startsWith(dir + '/'));
      return !isInMatchedDir;
    });
  }

  async writeFile(parentHandle: FileSystemDirectoryHandle, name: string, data: any): Promise<void> {
    const fileHandle = await parentHandle.getFileHandle(name, { create: true });
    const writable = await fileHandle.createWritable();
    await (writable as any).write(data);
    await (writable as any).close();
  }

  async createDirectory(parentHandle: FileSystemDirectoryHandle, name: string): Promise<FileSystemDirectoryHandle> {
    return await parentHandle.getDirectoryHandle(name, { create: true });
  }

  async getDirectoryHandle(parentHandle: FileSystemDirectoryHandle, path: string): Promise<FileSystemDirectoryHandle> {
    const parts = path.split('/').filter(Boolean);
    let currentHandle = parentHandle;
    for (const part of parts) {
      currentHandle = await currentHandle.getDirectoryHandle(part, { create: true });
    }
    return currentHandle;
  }

  async deleteEntry(parentHandle: FileSystemDirectoryHandle, name: string, recursive = false): Promise<void> {
    return await parentHandle.removeEntry(name, { recursive });
  }

  async copyEntry(sourceHandle: FileSystemHandle, targetDirHandle: FileSystemDirectoryHandle, newName?: string, currentPath = '', onBytes?: (bytes: number) => void): Promise<void> {
    const name = newName || sourceHandle.name;
    const fullPath = currentPath ? `${currentPath}/${name}` : name;

    if (fullPath.length > 250) {
      throw new Error(`路径过长 (${fullPath.length} 字符)，请缩短文件夹名称`);
    }

    try {
      if (sourceHandle.kind === 'file') {
        const fileHandle = sourceHandle as FileSystemFileHandle;
        const file = await fileHandle.getFile();
        const targetFileHandle = await targetDirHandle.getFileHandle(name, { create: true });
        const writable = await targetFileHandle.createWritable();
        await file.stream().pipeTo(writable);
        onBytes?.(file.size);
      } else {
        const sourceDirHandle = sourceHandle as FileSystemDirectoryHandle;
        const newDirHandle = await targetDirHandle.getDirectoryHandle(name, { create: true });

        const entries: FileSystemHandle[] = [];
        for await (const entry of (sourceDirHandle as any).values()) {
          entries.push(entry);
        }

        const CONCURRENCY = (navigator.hardwareConcurrency || 4) * 2;
        const active = new Set<Promise<void>>();

        for (const entry of entries) {
          const task = (async () => {
            if (entry.kind === 'file') {
              const file = await (entry as FileSystemFileHandle).getFile();
              const targetFileHandle = await newDirHandle.getFileHandle(entry.name, { create: true });
              const writable = await targetFileHandle.createWritable();
              await file.stream().pipeTo(writable);
              onBytes?.(file.size);
            } else {
              await this.copyEntry(entry, newDirHandle, undefined, fullPath, onBytes);
            }
          })().finally(() => active.delete(task));

          active.add(task);
          if (active.size >= CONCURRENCY) {
            await Promise.race(active);
          }
        }
        await Promise.all(active);
      }
    } catch (err: any) {
      console.error(`[copyEntry] 复制 ${name} 失败:`, err);
      throw err;
    }
  }

  // 批量并行复制多个项目到目标目录
  async copyEntriesParallel(
    items: { handle: FileSystemHandle; targetDir: FileSystemDirectoryHandle }[],
    onBytes?: (bytes: number) => void
  ): Promise<void> {
    const CONCURRENCY = navigator.hardwareConcurrency || 8;
    const active = new Set<Promise<void>>();
    for (const item of items) {
      const task = this.copyEntry(item.handle, item.targetDir, undefined, '', onBytes).finally(() => active.delete(task));
      active.add(task);
      if (active.size >= CONCURRENCY) await Promise.race(active);
    }
    await Promise.all(active);
  }

  async moveEntry(sourceHandle: FileSystemHandle, targetDirHandle: FileSystemDirectoryHandle, newName?: string): Promise<void> {
    const name = newName || sourceHandle.name;
    // Try native move if available (Chrome 100+)
    if ('move' in sourceHandle && typeof (sourceHandle as any).move === 'function') {
      await (sourceHandle as any).move(targetDirHandle, name);
    } else {
      // Fallback: Copy then Delete
      await this.copyEntry(sourceHandle, targetDirHandle, name);
      // We need the parent handle to delete, which we don't have here easily.
      // In our app context, we usually call deleteEntry from the store/hook where we have the parent.
      throw new Error('Native move not supported and parent handle missing for fallback');
    }
  }

  // Helper to extract TAR data to a directory handle
  async extractTarToDirectory(tarData: Uint8Array, targetDirHandle: FileSystemDirectoryHandle, onProgress?: (msg: string) => void): Promise<void> {
    const cpuCores = navigator.hardwareConcurrency || 8;
    const WRITE_CONCURRENCY = Math.min(50, Math.max(4, cpuCores * 2));
    
    const files: { name: string, start: number, size: number }[] = [];
    const directories = new Set<string>();
    let pos = 0;

    function parseOctal(bytes: Uint8Array) {
      const str = new TextDecoder().decode(bytes).replace(/\0/g, '').trim();
      return str ? parseInt(str, 8) : 0;
    }

    while (pos + 512 <= tarData.length) {
      const header = tarData.subarray(pos, pos + 512);
      pos += 512;

      if (header.every(b => b === 0)) break;

      let fileName = new TextDecoder().decode(header.subarray(0, 100)).replace(/\0/g, '');
      const ustarMagic = new TextDecoder().decode(header.subarray(257, 262));
      if (ustarMagic === 'ustar') {
        const prefix = new TextDecoder().decode(header.subarray(345, 500)).replace(/\0/g, '');
        if (prefix) fileName = prefix + '/' + fileName;
      }

      const fileSize = parseOctal(header.subarray(124, 136));
      const typeFlag = header[156];
      const isDirectory = typeFlag === 53 || fileName.endsWith('/');

      fileName = fileName.replace(/^\.\//, '').replace(/\/$/, '');

      if (fileName) {
        if (isDirectory) {
          directories.add(fileName);
        } else if (fileSize > 0) {
          files.push({ name: fileName, start: pos, size: fileSize });
          const parts = fileName.split('/');
          parts.pop();
          for (let i = 1; i <= parts.length; i++) {
            directories.add(parts.slice(0, i).join('/'));
          }
        }
      }

      pos += fileSize;
      pos += (512 - (fileSize % 512)) % 512;
    }

    if (onProgress) onProgress(`解析完成: ${files.length} 个文件, ${directories.size} 个目录`);

    const sortedDirs = Array.from(directories).filter(d => d).sort((a, b) => a.split('/').length - b.split('/').length);
    const dirHandleCache = new Map<string, FileSystemDirectoryHandle>();
    dirHandleCache.set('', targetDirHandle);

    for (const dir of sortedDirs) {
      const parts = dir.split('/');
      const parentPath = parts.slice(0, -1).join('/');
      const dirName = parts[parts.length - 1];
      const parentHandle = dirHandleCache.get(parentPath) || targetDirHandle;
      try {
        const handle = await parentHandle.getDirectoryHandle(dirName, { create: true });
        dirHandleCache.set(dir, handle);
      } catch (e) {
        console.error(`Failed to create directory ${dir}`, e);
      }
    }

    const promises = new Set<Promise<void>>();
    let completed = 0;

    for (const file of files) {
      const parts = file.name.split('/');
      const baseName = parts.pop()!;
      const parentPath = parts.join('/');
      const parentHandle = dirHandleCache.get(parentPath) || targetDirHandle;

      const task = (async () => {
        const fileHandle = await parentHandle.getFileHandle(baseName, { create: true });
        const writable = await fileHandle.createWritable();
        await (writable as any).write(tarData.subarray(file.start, file.start + file.size));
        await (writable as any).close();
        completed++;
      })();

      promises.add(task);
      task.finally(() => promises.delete(task));

      if (promises.size >= WRITE_CONCURRENCY) {
        await Promise.race(promises);
      }
    }

    await Promise.all(promises);
    if (onProgress) onProgress(`✓ 成功提取 ${completed} 个文件`);
  }
}

export const fileSystemService = FileSystemService.getInstance();
